import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { PlanService } from '@services/plan.service';
import { SharedService } from '@services/shared.service';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { AuthService } from 'src/app/Auth/AuthService';
import { startCheckout } from 'src/app/utils/checkout-owner.util';

@Component({
  selector: 'app-upgrade',
  standalone: true,
  imports: [
    NzSelectModule,
    FormsModule,
    CommonModule,
    RouterLink,
    ReactiveFormsModule,
  ],
  templateUrl: './upgrade.component.html',
  styleUrls: ['./upgrade.component.scss'],
})
export class UpgradeComponent {
  selectedValue = null;
  countryRecords!: any[];
  selectedPayment = 'credit-card';
  sharedService = inject(SharedService);
  authService = inject(AuthService);
  planService = inject(PlanService);
  router = inject(Router);
  fb = inject(FormBuilder);
  selectedSubscription: any = {
    plan_id: '',
    mode: 'monthly',
    price: ''
  };
  originalPrice: number = 0;
  billingForm: any;

  /** Always true: this screen is switched off, see the note inside. */
  private redirectUnavailable(): boolean {
    // The card form on this page posted to BANK_CARD, which is switched off on
    // the gateway (no raw card numbers are collected or stored). Plan changes are
    // paid through Stripe Checkout from the subscription page.
    this.sharedService.createNotification(
      'info',
      'Please choose your plan on the subscription page and pay securely at checkout.'
    );
    this.router.navigate(['/home/subscription']);
    return true;
  }

  ngOnInit() {
    // Build the form first so the template never binds to an undefined group while the redirect runs.
    this.initializeForm();
    if (this.redirectUnavailable()) return;
    this.countryList();

    this.billingForm.get('user_id').setValue(this.authService.getUserId());
    this.billingForm.get('email').setValue(this.authService.getUserEmail());

    const selectedPlan = this.planService.getPlan();

    if (!selectedPlan) {
      // Redirect back if no plan selected
      this.router.navigate(['/home/subscription']);
      return;
    } else {
      this.selectedSubscription.plan_id = selectedPlan.plan_id;
      this.selectedSubscription.mode = selectedPlan.mode;
      this.selectedSubscription.price = selectedPlan.price;
      this.originalPrice = Number(this.selectedSubscription.price);

      this.selectedSubscription = {
        ...this.selectedSubscription,
        price: Number(this.selectedSubscription.price),
      };
    }
  }

  selectPaymentMethod(method: string) {
    this.selectedPayment = method;
    this.billingForm.patchValue({ payment_method: method });
  }

  selectSubscription(mode: 'monthly' | 'annually') {
    this.selectedSubscription = {
      ...this.selectedSubscription,
      mode,
    };
  }

  formatExpiry(event: any) {
    let input = event.target.value;
    const isBackspace = event.inputType === 'deleteContentBackward'; // Check if backspace was pressed

    // Remove non-digits
    input = input.replace(/\D/g, '');

    if (input.length >= 2) {
      const month = input.substring(0, 2);
      const year = input.substring(2);

      // Format as MM/YY, but avoid adding "/" again if Backspace was pressed
      input = isBackspace && input.length <= 2 ? month : `${month}/${year}`;
    }

    event.target.value = input;
  }

  formatCvv(event: any) {
    let input = event.target.value;

    input = input.replace(/\D/g, '');
    if (input.length > 3) {
      input = input.substring(0, 3);
    }

    event.target.value = input;
  }

  formatBankNumber(event: any) {
    let input = event.target.value;

    input = input.replace(/\D/g, '');
    input = input.replace(/(\d{4})(?=\d)/g, '$1 ');
    if (input.length > 19) {
      input = input.substring(0, 19);
    }

    event.target.value = input.trim();
  }

  countryList() {
    this.sharedService.getCountryRecords().then((data) => {
      this.countryRecords = data;
    });
  }

  initializeForm() {
    this.billingForm = this.fb.group({
      user_id: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      payment_method: ['credit-card', Validators.required],
      card_number: [
        '',
        [Validators.required, Validators.pattern(/^\d{4} \d{4} \d{4} \d{4}$/)],
      ],
      expiry: [
        '',
        [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/)],
      ],
      cvv: ['', [Validators.required, Validators.pattern(/^\d{3}$/)]],
      country: ['', Validators.required],
      zip_code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      set_primary: [false],
    });
  }

  submitForm() {
    this.sharedService.showLoader();

    if (this.billingForm.valid) {
      const expiryValue = this.billingForm.get('expiry')?.value;

      if (expiryValue) {
        const [month, year] = expiryValue.split('/');
        const expiry = new Date(2000 + parseInt(year), parseInt(month) - 1);
        const today = new Date();

        if (expiry < today) {
          this.sharedService.createNotification('error', 'Card has expired');
          this.sharedService.hideLoader();
          return;
        }
      }

      // Step 1: Add card
      this.authService.login('BANK_CARD', this.billingForm.value).subscribe({
        next: (response: any) => {
          // If BANK_CARD succeeds, proceed with subscription
          this.subscribAPlan(
            this.selectedSubscription.plan_id,
          );
        },
        error: (error: any) => {
          this.sharedService.hideLoader();
          this.sharedService.createNotification(
            'error',
            error?.error?.message || 'Card could not be added. Please try again.'
          );
          console.error('BANK_CARD error:', error);
        },
      });
    } else {
      const invalidFields: string[] = [];

      Object.keys(this.billingForm.controls).forEach((key) => {
        const control = this.billingForm.get(key);
        if (control && control.invalid) {
          invalidFields.push(key);
          control.markAsTouched();
        }
      });

      const errorMessage = `Please correct the following fields: ${invalidFields.join(
        ', '
      )}`;
      this.sharedService.createNotification('error', errorMessage);
      this.sharedService.hideLoader();
    }
  }

  subscribAPlan(plan_id: string,) {

    const type = {
      plan_id,
      success_url: `${this.sharedService.getBaseUrl()}/auth/payment-approved?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${this.sharedService.getBaseUrl()}/home`,
    };

    this.sharedService.showLoader();
    this.authService.login('CHOOSE_SUBSCRIPTION_PLAN', type).subscribe({
      next: (Response: any) => {
        setTimeout(() => {
          this.sharedService.hideLoader();
          if (Response.checkout_url) {
            startCheckout(Response.checkout_url);
          } else {
            this.sharedService.createNotification(
              'error',
              'Checkout URL not found.'
            );
          }
        }, 1000);
        const alertsJson = localStorage.getItem('app_alerts');

        if (alertsJson) {
          let alerts = JSON.parse(alertsJson);

          // Remove alert where action === "verify"
          alerts = alerts.filter(
            (alert: any) => alert.action !== 'subscription'
          );

          // Save updated alerts back to localStorage
          localStorage.setItem('app_alerts', JSON.stringify(alerts));
        }
        this.sharedService.closeAlertByAction('subscription');
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          error?.error?.message || 'Failed to initiate subscription.'
        );
        console.error('Subscription error:', error);
      },
    });
  }
}
